/* eslint-disable no-unused-vars */

import { useEffect, useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import _ from 'lodash';
import { store } from 'redux/store';

import PageContainer from '@components/page-container';
import PageHeader from '@components/page-header';
import NewTabs from '@components/new-tabs';
import MoreInformation from '@components/more-information';
import PipelineSite from './pipeline-site';
import WorkOrder from '../../site-details/tasks/work-order';
import Icon from '@components/icon-component';
import LoadingRound from '@components/loading-round/loading-round';
import PipeLineCard from '@components/pipeline-listing/pipeline-listing-inner/pipeline-card';
import PipelineBookMark from '@components/pipeline-listing/pipeline-bookmark';
import BookmarkErrorScreen from '@components/bookmark-error-screen';

import { getAppDispatch } from '@utilities/dispatch.util';
import { backHistoryActions } from '@redux/slices/back-history';
import { getPipelineDetails } from './helper';
import { filterActions } from '@redux/slices/filters';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { PERMISSIONS } from 'permissions/constants';

import './pipeline-site.scss';
import style from './style.module.scss';
import EditPipeline from './edit-pipeline';
import { initWebWorker } from 'webworker';
import DeleteModal from '@components/modal/variants/delete-modal';
import { useToastContext } from '@context/toaster.context';
import BookmarksService from '@services/bookmarks.service';
import { bookmarksActions } from '@redux/slices/bookmarks';

const PipelineSiteDetails = () => {
  const [searchParams] = useSearchParams();
  const dispatch = getAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { ToasterUtil } = useToastContext();

  const { width } = useWindowDimensions();

  const swipeAbleMenuRef = useRef(null);
  const mainDivRef = useRef(null);
  const siteCardRef = useRef(null);
  const assetDetailPageHeaderRef = useRef(null);

  const [tab, setTab] = useState('0');
  const [editPipeline, setEditPipeline] = useState(false);
  const [open, setOpen] = useState(false);
  const [moreInfo, setMoreInfo] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [allTask, setAllTask] = useState(false);
  const [checks, setChecks] = useState(false);
  const [inspections, setInspections] = useState(false);
  const [workOrder, setWorkOrder] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [pipelineDetails, setPipelineDetails] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [workRequest, setWorkRequest] = useState(false);
  const [deleteBookmark, setDeleteBookmark] = useState(false);

  const { sitesMap = {}, sites: cacheSites = [] } = useSelector((state) => state.cache);
  const { settings = {} } = useSelector((state) => state.settings);
  const pipelineDetailsInfo = useSelector((state) => state?.backHistory?.pipelineDetails);
  const { picklistsMap = {} } = useSelector((state) => state.picklists);

  const { tasksFilterCount = 0 } = useSelector((state) => state.filters);
  const [isFilterActive, setFilterActive] = useState(width < 768 ? false : true);

  const { id } = useParams();
  const bookmarkName = searchParams.get('bookmarkName');
  const bookmarkId = searchParams.get('bookmarkId');
  const isBookmarkAtAGlance = searchParams.get('isBookmarkAtAGlance') || false;

  const currentOpenTaskTab = searchParams.get('currentOpenTaskTab') || '';

  useEffect(() => {
    if (currentOpenTaskTab) {
      if (currentOpenTaskTab === 'allTask') setAllTask(true);
      else if (currentOpenTaskTab === 'workOrder') setWorkOrder(true);
      else if (currentOpenTaskTab === 'inspections') setInspections(true);
      else if (currentOpenTaskTab === 'checks') setChecks(true);
      // else if (currentOpenTaskTab === 'workRequest') setWorkRequest(true);
    }
  }, [currentOpenTaskTab]);

  const { tasksList: reduxTasksList = [], selectTask } = useSelector((state) => state.tasks);
  const { inspectionsList } = useSelector((state) => state.inspection);
  const { checksList } = useSelector((state) => state.checks);
  const { utFormDownloadingLoading } = useSelector((state) => state.utFormData) || {};

  useEffect(() => {
    if (!_.isEmpty(pipelineDetailsInfo)) {
      pipelineDetailsInfo?.pipelineDetailsTab &&
        setTab(pipelineDetailsInfo?.pipelineDetailsTab || '0');
      pipelineDetailsInfo?.workOrder && setWorkOrder(pipelineDetailsInfo?.workOrder);
    }
  }, [pipelineDetailsInfo]);

  useEffect(() => {
    if (utFormDownloadingLoading) {
      return;
    }

    initWebWorker({
      args: {
        store: { ...store.getState() },
        id,
        getWorkRequestData: true
      },
      type: 'get-pipeline-details'
    })
      .then((data) => {
        setPipelineDetails(data?.result[0]);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, [id, syncComplete, reduxTasksList, inspectionsList, checksList, utFormDownloadingLoading]);

  const {
    allDoneTasks = [],
    allTasksAndInspectionsForPipeline,
    dataPoints,
    fromLocation,
    MoreInfoDataPointsMap,
    isOverDue,
    licenseNumber,
    lineNumber,
    status: _status,
    toLocation,
    siteIds = [],
    allWorkOrder,
    allDoneWorkOrder,
    isWorkOrderOverDue,
    allInspections,
    allDoneInspection,
    isInspectionOverDue,
    allChecks,
    allDoneCheck,
    isCheckOverDue,
    link,
    newWorkRequestList,
    allOnDemandRecordCount
  } = useMemo(() => {
    return pipelineDetails || {};
  }, [pipelineDetails]);

  useMemo(() => {
    if (!isLoading) {
      if (!id) return <Navigate to={'/pipelines'} />;
    }
  }, [pipelineDetails, isLoading]);

  const status = picklistsMap[_status]?.value || '';

  useEffect(() => {
    if (syncComplete) setSyncComplete(false);
  }, [syncComplete]);

  const arrayText = [
    {
      icon: <Icon name={'more-info'} />,
      text: 'More information',
      onClick: () => {
        setMoreInfo(true);
        setOpen(false);
      }
    },
    {
      icon: <Icon name={'bookmark-grey'} />,
      text: 'Bookmark',
      onClick: () => {
        setOpenModal(true);
        setOpen(false);
      }
    },
    ...(width < 600
      ? []
      : [
          {
            icon: <Icon name={'open-in-oplii-desktop'} />,
            text: 'Open in Oplii desktop',
            onClick: () => {
              window.open(link, '_blank');
            }
          }
        ])
  ];

  useEffect(() => {
    if (editPipeline) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
  }, [editPipeline]);

  const onParentClickEvent = ({ workOrderTab, workOrderSortTab }) => {
    dispatch?.(
      backHistoryActions.setPipelineDetailsPageHistory({
        workOrder,
        pipelineDetailsTab: tab,
        workOrderTab,
        workOrderSortTab
      })
    );
  };

  const pageHeaderClickEvent = () => {
    workOrder && dispatch?.(filterActions.setTaskFilters(null));
    if (moreInfo) setMoreInfo(false);
    else if (allTask) setAllTask(false);
    else if (checks) setChecks(false);
    else if (inspections) setInspections(false);
    else if (workOrder) setWorkOrder(false);
    else if (workRequest) setWorkRequest(false);

    dispatch?.(backHistoryActions.setPipelineDetailsPageHistory(null));
  };

  const pageHeaderTitle = useMemo(() => {
    return `${
      bookmarkName
        ? bookmarkName
        : // : moreInfo
        // ? 'Pipeline Information'
        allTask
        ? `Tasks, records (${
            allTasksAndInspectionsForPipeline?.length || allOnDemandRecordCount
              ? allTasksAndInspectionsForPipeline.length + allOnDemandRecordCount
              : ''
          })`
        : checks
        ? `Checks (${allChecks?.length ? allChecks.length : ''})`
        : workOrder
        ? `Work orders (${
            allWorkOrder?.length || pipelineDetails?.newWorkOrderList?.length
              ? allWorkOrder.length + pipelineDetails?.newWorkOrderList?.length
              : ''
          })`
        : workRequest
        ? `Work requests (${newWorkRequestList?.length > 0 ? newWorkRequestList?.length : ''})`
        : inspections
        ? `Inspections (${allInspections?.length ? allInspections.length : ''})`
        : 'Pipeline'
    }`;
  }, [
    bookmarkName,
    moreInfo,
    allTask,
    checks,
    workOrder,
    allTasksAndInspectionsForPipeline,
    inspections,
    newWorkRequestList,
    workRequest,
    allOnDemandRecordCount,
    pipelineDetails
  ]);

  const pipelineMoreInfo = useMemo(() => {
    if (MoreInfoDataPointsMap) {
      const pipelineMoreInfoOrder = [
        'Pipeline #',
        'License # - line #',
        'Type',
        'Status',
        'From location',
        'To location',
        'Area',
        'Material',
        'Length',
        'Substance',
        'Internal protection',
        'Approval #'
      ];

      const info = Object?.keys(MoreInfoDataPointsMap)?.map((a) => {
        return {
          title:
            MoreInfoDataPointsMap[a]?.title === 'Length'
              ? `${MoreInfoDataPointsMap[a]?.value} ${
                  settings['distanceUnit'] === 'kilometers' ? 'km' : 'mi'
                }`
              : MoreInfoDataPointsMap[a]?.value || null,
          heading: MoreInfoDataPointsMap[a]?.title || null
        };
      });

      return _.sortBy(info, (obj) => {
        return _.indexOf(pipelineMoreInfoOrder, obj.heading);
      });
    }
    return [];
  }, [MoreInfoDataPointsMap]);

  const siteDetailsPageClassNames = useMemo(() => {
    return `${moreInfo || selectTask ? style.pageListingsWithMoreInfo : style.pageListings} ${
      style.noScroll
    } ${(workOrder || allTask || checks || inspections || workRequest) && style.noScroll} 
`;
  }, [workOrder, allTask, checks, inspections, moreInfo, selectTask, workRequest]);

  const pipelineNotFoundInBookmark = useMemo(() => {
    if (bookmarkName && !isLoading) {
      if (licenseNumber && lineNumber) return false;
      else return true;
    }
    return false;
  }, [bookmarkName, isLoading, licenseNumber, lineNumber]);

  const allInspectionsList = useMemo(() => {
    return [
      ...(pipelineDetails?.allInspections || []),
      ...(pipelineDetails?.newInspectionList || [])
    ];
  }, [inspections, pipelineDetails]);

  const allTasksList = useMemo(() => {
    return [
      ...(newWorkRequestList || []),
      ...(pipelineDetails?.newWorkOrderList || []),
      ...(allTasksAndInspectionsForPipeline || [])
    ];
  }, [newWorkRequestList, pipelineDetails, allTasksAndInspectionsForPipeline]);

  const allWorkOrderList = useMemo(() => {
    return [...(allWorkOrder || []), ...(pipelineDetails?.newWorkOrderList || [])];
  }, [workOrder, pipelineDetails, allWorkOrder]);

  const allChecksList = useMemo(() => {
    return [...(allChecks || []), ...(pipelineDetails?.newChecksList || [])];
  }, [checks, pipelineDetails, allChecks]);

  useEffect(() => {
    if (!isLoading && !status) {
      navigate('/error?assetNotFound=true');
    }
  }, [isLoading, status]);

  const handleInfoClick = () => {
    const infoTabIndex = items?.findIndex((x) => x?.label === 'Info');
    setTab(infoTabIndex);
  };

  const handleBookmarkClick = () => {
    setOpenModal(true);
  };

  const PipelineCardComponent = ({ moreInfoTabActive, showCard = false, className = '', isInfo }) =>
    showCard ? (
      <div className={`marginClass ${className}`} ref={siteCardRef}>
        <PipeLineCard
          toLocation={toLocation}
          title={`${licenseNumber} - ${lineNumber}`}
          pipelineDetails
          fromLocation={fromLocation}
          dataPoints={dataPoints || []}
          allTasks={allTasksAndInspectionsForPipeline}
          allDoneTasks={allDoneTasks}
          isOverDue={isOverDue}
          status={status}
          arrayText={arrayText}
          sites={
            siteIds?.length > 0
              ? siteIds?.map((x) => {
                  return {
                    ...cacheSites[sitesMap[x]],
                    link: `/site-details/${x}`
                  };
                })
              : []
          }
          open={open}
          setOpen={setOpen}
          moreInfo={moreInfo}
          link={''}
          allOnDemandRecordCount={allOnDemandRecordCount}
          onInfoCLick={handleInfoClick}
          onBookmarkCLick={handleBookmarkClick}
          infoTabActive={moreInfoTabActive}
          toDesktopLink={link && width >= 768 ? link : undefined}
          otherTabsView={allTask || checks || workOrder || workRequest || inspections}
          className={`${isInfo && style.borderBottomNone}`}
        />
      </div>
    ) : (
      <></>
    );

  const items = useMemo(() => {
    return !isLoading
      ? [
          ...(allTasksAndInspectionsForPipeline?.length > 0 || allOnDemandRecordCount > 0
            ? [
                {
                  label: `Tasks + records (${
                    allTasksAndInspectionsForPipeline?.length + allOnDemandRecordCount
                  })`,
                  keyName: 'tasks',
                  children: (
                    <PipelineSite
                      setChecks={setChecks}
                      setAllTask={setAllTask}
                      setWorkOrder={setWorkOrder}
                      setInspections={setInspections}
                      allWorkOrder={allWorkOrder}
                      allDoneWorkOrder={allDoneWorkOrder}
                      allInspection={allInspections}
                      allDoneInspection={allDoneInspection}
                      allTasks={allTasksAndInspectionsForPipeline}
                      allDoneTasks={allDoneTasks}
                      isOverDue={isOverDue}
                      isWorkOrderOverDue={isWorkOrderOverDue}
                      isInspectionOverDue={isInspectionOverDue}
                      allChecks={allChecks}
                      allDoneCheck={allDoneCheck}
                      isCheckOverDue={isCheckOverDue}
                      newWorkRequestList={newWorkRequestList}
                      setWorkRequest={setWorkRequest}
                      allOnDemandRecordCount={allOnDemandRecordCount || 0}
                      newWorkOrderList={pipelineDetails?.newWorkOrderList || []}
                      newInspectionList={pipelineDetails?.newInspectionList || []}
                      newChecksList={pipelineDetails?.newChecksList || []}
                      card={() => (
                        <PipelineCardComponent
                          moreInfoTabActive={false}
                          showCard={width <= 768 ? true : false}
                          className={`mb16 ${style.pt0}`}
                        />
                      )}
                    />
                  )
                }
              ]
            : []),

          ...(pipelineMoreInfo
            ? [
                {
                  label: `Info`,
                  keyName: 'info',
                  children: (
                    <div className={style.infoTab} id="pipeline-info-tab">
                      <PipelineCardComponent
                        moreInfoTabActive={true}
                        showCard={width <= 768 ? true : false}
                        className={` ${style.pt0}`}
                        isInfo
                      />
                      <div className={style.addMarginBottom}>
                        <MoreInformation info={pipelineMoreInfo} hideEditBtn />
                      </div>
                    </div>
                  )
                }
              ]
            : [])
        ]
      : [];
  }, [
    isLoading,
    isFilterActive,
    allTasksAndInspectionsForPipeline,
    allDoneTasks,
    pipelineMoreInfo,
    allTasksAndInspectionsForPipeline,
    newWorkRequestList,
    allOnDemandRecordCount
  ]);

  useEffect(() => {
    if (items?.length > 0) {
      const params = new URLSearchParams(location.search);
      const currentTab = parseInt(tab);
      params.set('tab', currentTab);

      if (items[currentTab]?.keyName === 'tasks') {
        if (allTask) params.set('currentOpenTaskTab', 'allTask');
        else if (workOrder) params.set('currentOpenTaskTab', 'workOrder');
        else if (inspections) params.set('currentOpenTaskTab', 'inspections');
        else if (checks) params.set('currentOpenTaskTab', 'checks');
        else if (workRequest) params.set('currentOpenTaskTab', 'workRequest');
        else if (!workOrder && !allTask && !inspections && !checks)
          params.delete('currentOpenTaskTab');
      }

      params.set(
        'virtualScrollTab',
        currentTab === 0 && items[currentTab]?.keyName === 'tasks'
          ? -1
          : allTasksAndInspectionsForPipeline?.length > 0
          ? currentTab - 1
          : currentTab
      );
      params.set('keyName', items[currentTab]?.keyName);
      const tabParams = '?' + params.toString();
      const prevRoute = `${location.pathname}${tabParams}`;
      navigate(prevRoute, { replace: true });
    }
  }, [tab, items, workOrder, allTask, inspections, workRequest]);

  const onDeleteBookmark = async () => {
    try {
      if (!bookmarkId) {
        ToasterUtil.showError('Unable to delete bookmark: Bookmark ID is missing.');
        return;
      }

      await BookmarksService.deleteBookmarks({ bookmarkId: bookmarkId });

      dispatch?.(bookmarksActions.setBookmarkValidity(true));

      ToasterUtil.showSuccess('Bookmark deleted successfully');

      const params = new URLSearchParams(location.search);

      params.set('from', 'site');
      params.delete('bookmarkName');
      params.delete('bookmarkId');

      setDeleteBookmark(false);

      const newUrl = `${location.pathname}?${params.toString()}`;

      // Use navigate with replace
      navigate(newUrl, { replace: true });
    } catch (error) {
      ToasterUtil.showError('Failed to delete bookmark');
    }
  };

  const { moreInfoTabActive, isTasksTabActive } = useMemo(() => {
    const parseTab = parseInt(tab);
    return {
      moreInfoTabActive: items[parseTab]?.keyName === 'info' ? true : false,
      isTasksTabActive: items[parseTab]?.keyName === 'tasks' ? true : false
    };
  }, [items, tab]);

  const swipeable = document.getElementById('react-swipeable-view-container') || null;
  const pipelineInfoTab = document.getElementById('pipeline-info-tab') || null;
  const tasksInfoTab = document.getElementById('tasks-info-tab') || null;

  useEffect(() => {
    if (swipeable) {
      if (moreInfoTabActive) {
        swipeable.style.background = '#ffffff';
      } else {
        swipeable.style.background = '';
      }
    }
  }, [moreInfoTabActive, swipeable]);

  const onScrollParentEvent = (event) => {
    const currentScrollPosition = event.target.scrollTop || 0;
    const siteCardOffsetHeight = siteCardRef?.current?.offsetHeight || 0;

    if (siteCardRef?.current) {
      const topHeaderHeight = width > 769 ? 94 : 86;
      const siteCardAndMarginCalculate = siteCardOffsetHeight || 0;
      const navigationTabsHeight = 33;
      const bottomFooterHeight = width > 769 ? 0 : 84;
      const totalHeight =
        topHeaderHeight + siteCardAndMarginCalculate + bottomFooterHeight + navigationTabsHeight;

      const container =
        moreInfoTabActive && pipelineInfoTab
          ? pipelineInfoTab
          : isTasksTabActive && tasksInfoTab
          ? tasksInfoTab
          : null;
      if (container && (moreInfoTabActive || isTasksTabActive)) {
        container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition}px)`;
      }

      const containerIds = [
        'structure-listing-pages-container',
        'site-listing-pages-container',
        'pipeline-listing-pages-container',
        'equipment-listing-pages-container'
      ];

      containerIds.forEach((id) => {
        const container = document.getElementById(id);
        if (container) {
          container.style.height = `calc(100dvh - ${totalHeight - currentScrollPosition + 10}px)`;
        }
      });
    }

    const tasksRecordsListsTabs = document?.querySelectorAll('#tasksRecordsListsTabs');
    for (let a = 0; a < tasksRecordsListsTabs.length; a++) {
      const totalHeight =
        tasksRecordsListsTabs[a]?.getAttribute('data-pageHeightOnDetailsPage') || 0;
      tasksRecordsListsTabs[a].style.height = `calc(100svh  - ${
        totalHeight - currentScrollPosition
      }px)`;
    }
  };

  const isFilterIconIsRequired = useMemo(() => {
    return (!items[tab]?.keyName?.toLowerCase()?.includes('tasks') &&
      !items[tab]?.keyName?.toLowerCase()?.includes('info')) ||
      allTask ||
      checks ||
      inspections ||
      workOrder ||
      workRequest
      ? setFilterActive
      : false;
  }, [tab, items, allTask, checks, inspections, workOrder, workRequest]);

  return (
    <>
      {!isLoading && pipelineNotFoundInBookmark ? (
        <BookmarkErrorScreen />
      ) : (
        <PageContainer moreInfo={moreInfo}>
          <PageHeader
            disableBack={allTask || checks || inspections || workOrder || workRequest}
            title={pageHeaderTitle}
            {...(isFilterIconIsRequired && {
              setFilterActive: isFilterIconIsRequired
            })}
            onClick={pageHeaderClickEvent}
            count={tasksFilterCount}
            ref={assetDetailPageHeaderRef}
          />

          {isLoading ? (
            <div style={{ backgroundColor: 'transparent', width: '100%' }}>
              <LoadingRound />
            </div>
          ) : (
            <div>
              <div
                ref={mainDivRef}
                id="assetDetailPage"
                className={siteDetailsPageClassNames}
                style={{ position: 'relative', paddingBottom: '0px', marginTop: '0px' }}
                onScroll={onScrollParentEvent}
              >
                <PipelineCardComponent
                  moreInfoTabActive={moreInfoTabActive}
                  showCard={width > 768 ? true : false}
                />

                <div className="flex relative">
                  <div
                    style={{
                      marginTop: '0px',
                      overflow: 'hidden',
                      paddingBottom: '0px'
                    }}
                    className={`${
                      moreInfo ? style.pageListingsWithMoreInfo : style.pageListings
                    } mobileMargin`}
                  >
                    {allTask || inspections || workOrder || checks || workRequest ? (
                      <WorkOrder
                        asset="pipeline"
                        tasks={
                          inspections
                            ? allInspectionsList
                            : allTask
                            ? allTasksList
                            : workOrder
                            ? allWorkOrderList
                            : checks
                            ? allChecksList
                            : workRequest
                            ? newWorkRequestList
                            : []
                        }
                        workRequest={workRequest}
                        isFilterActiveHeader={isFilterActive}
                        setFilterActiveHeader={setFilterActive}
                        setSyncComplete={setSyncComplete}
                        selectTodoOrInProgressStatus={!_.isEmpty(bookmarkName) ? true : false}
                        onParentClickEvent={onParentClickEvent}
                        workOrderSelectedTab={pipelineDetails?.workOrderTab || null}
                        workOrderSortTab={pipelineDetails?.workOrderSortTab || null}
                        siteDetails
                        filterType={
                          allTask
                            ? 'all'
                            : inspections
                            ? 'inspection'
                            : workOrder
                            ? 'workOrder'
                            : checks
                            ? 'check'
                            : workRequest
                            ? 'workRequest'
                            : null
                        }
                      />
                    ) : items?.length > 0 ? (
                      <div
                        className={`assetDetailNavNotFixed`}
                        ref={swipeAbleMenuRef}
                        id="assetDetailsNavigation"
                      >
                        <NewTabs
                          active={tab}
                          tabs={items}
                          setActive={(tab) => {
                            setTab(tab);
                          }}
                          className={`${style.mobileMargin}`}
                          swipeAbleNavbarWhiteBackground={moreInfoTabActive ? true : false}
                        />
                      </div>
                    ) : (
                      <>
                        <div
                          className="no-result"
                          style={{ flex: 1, justifyContent: 'flex-start', gap: '20px' }}
                        >
                          <div>No results</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {/* )} */}
              </div>
            </div>
          )}

          {openModal && (
            <PipelineBookMark
              open={openModal}
              setOpen={(value) => {
                setOpenModal(value);
              }}
              placeholder="My favourite truck"
              type="Pipeline Details"
              pipelineId={id || null}
              editMode={bookmarkId}
              bookmarkPrefillName={bookmarkName}
              isBookmarkAtAGlance={isBookmarkAtAGlance}
              onDelete={() => {
                setDeleteBookmark(true);
                setOpenModal(false);
              }}
            />
          )}
          {deleteBookmark && (
            <DeleteModal
              open={deleteBookmark}
              setOpen={setDeleteBookmark}
              handleDelete={onDeleteBookmark}
              confirmDeletionBookmark
              recordType="Bookmark"
            />
          )}

          {editPipeline && (
            <EditPipeline
              open={editPipeline}
              closedModal={() => setEditPipeline((prev) => !prev)}
              pipelineDetails={pipelineDetails}
            />
          )}
        </PageContainer>
      )}
    </>
  );
};

export default PipelineSiteDetails;
